import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTypedSearchParams } from 'react-router-typesafe-routes';
import AppCell from '@/components/common/app-cell';
import Toggle from '@/components/common/toggle';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import { useCommDispatcher } from '@/hooks/comm/use-comm';
import useClearAllDeviceSession from '@/hooks/conversations/use-clear-all-device-session';
import useAsyncValue from '@/hooks/use-async-value';
import useUserPlatform from '@/hooks/use-user-platform';
import ROUTES from '@/routes';
import { useConfigurationStore } from '@/stores/configuration';
import { useMessageStore } from '@/stores/message';
import { makePrompt } from '@/stores/prompter';
import cn from '@/utils/merge-class-names';
import { estimateStorageQuota } from '@/utils/storage';

export default function MessagingSettingsRoute() {
  const queryClient = useQueryClient();
  const { clearAllDeviceSession } = useClearAllDeviceSession();
  const { enterAsSubmit, setEnterAsSubmit } = useConfigurationStore();
  const { t } = useTranslation();
  const storage = useAsyncValue(() => estimateStorageQuota());
  const { dispatch } = useCommDispatcher();
  const { isEmbedded } = useUserPlatform();
  const [searchParams] = useTypedSearchParams(ROUTES.MY.SETTINGS.MESSAGING);

  useEffect(() => {
    if (isEmbedded) {
      useConfigurationStore.persist.rehydrate();
    }
  }, []);

  const handleClearAllMessages = () =>
    makePrompt({
      key: 'clear-all-messages',
      title: t('messaging-settings.clear-chat'),
      content: t('messaging-settings.clear-chat-confirm'),
      promise: () => clearAllDeviceSession(),
    });

  const handleClearStorage = () =>
    makePrompt({
      key: 'clear-storage',
      title: t('messaging-settings.trim-storage.title'),
      content: t('messaging-settings.trim-storage.description'),
      promise: async () => {
        queryClient.clear();
        await useMessageStore.getState().trim();
      },
    });

  return (
    <PageLayout
      title={t('mine.messaging')}
      className='mode-embedded:gap-0 mode-embedded:bg-neutral-100'
      headerConfig={{
        navigateBack: true,
        onNavigateBack: () => (searchParams.from === 'onboard' ? dispatch({ event: 'dismiss' }) : undefined),
      }}>
      <div className='flex flex-col mode-embedded:bg-neutral-0 mode-embedded:rounded-md mode-embedded:m-5'>
        <AppCell
          label={t('messaging-settings.enter-as-submit')}
          suffix={
            <Toggle
              checked={enterAsSubmit ?? false}
              onChange={(value) => setEnterAsSubmit(value)}
            />
          }
        />
        <AppCell
          label={t('messaging-settings.storage')}
          content={
            storage ? (
              <span className={cn('flex-col text-right', { 'text-error': storage.ratio > 50 })}>
                <div>{`${storage.ratio}%`}</div>
                {`(${storage.usage}/${storage.quota})`}
              </span>
            ) : (
              'N/A'
            )
          }
          onClick={handleClearStorage}
        />

        <AppCell
          labelClassName='mode-embedded:text-error'
          label={t('messaging-settings.clear-chat')}
          onClick={handleClearAllMessages}
        />
      </div>
    </PageLayout>
  );
}
